import { useState, useEffect, useRef } from 'react';
import type { RouteConfig } from '@/types/route';
import { useLocale } from '@/hooks/useLocale';
import { useThemeStore } from '../../../store/ConfigStore';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/Toast';
import { useStore } from '@/store';
import type { Dayjs } from 'dayjs';
import { Table, Input } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import GenericQueryCard, { QueryField } from '@/components/GenericQueryCard';
import { Pagination } from "@/components/Pagination";
import type { InvitationLink } from '@/types/invitation';
import { fetchLinkList, modifyInvitationLink } from '@/services/invitation';
import { MobileTableCard } from '@/components/MobileCards';
import dayjs from 'dayjs';
import InviteFriendDialog from './components/ShareLinkDialog';
import Dialog from '../../../components/Dialog';
import SvgIcon from '../../../components/SvgIcon';
import LineBarIcon from '../../../assets/svg/lineBar.svg';
import PencilIcon from '../../../assets/svg/pencil.svg';
import ArrowRightIcon from '../../../assets/svg/keyboard_arrow_right.svg';
import CopyIcon from '../../../assets/svg/copy.svg';
import CopyDarkIcon from '../../../assets/svg/copyDark.svg';
import CheckIcon from '../../../assets/svg/check-line.svg';
import CheckDarkIcon from '../../../assets/svg/check-line-dark.svg';
import { userInfo } from 'os';

export const routeConfig: RouteConfig = {
    title: 'route.invitationPromotion.invitationLink',
    layout: true,
    sort: 0
};

const InvitationLink = () => {
    const { t } = useLocale();
    const { UserStore } = useStore();
    const isDark = useThemeStore(); // 引入主题状态
    const toast = useToast();
    const navigate = useNavigate();

    const [code, setCode] = useState<string>('');
    const [selectedPromotionChannel, setSelectedPromotionChannel] = useState(0);
    // const [dataStatisticsTime, setDataStatisticsTime] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(1689081600);
    const [isCopied, setIsCopied] = useState(false);
    const [copiedCode, setCopiedCode] = useState<string | null>(null);
    const [invitationLinkData, setInvitationLinkData] = useState<InvitationLink[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [total, setTotal] = useState(0);

    // For managing settings dialog
    const [step, setStep] = useState<'details' | 'ratio' | 'code' | 'registration' | 'remark'>('details');
    const [settingsDialogVisible, setSettingsDialogVisible] = useState(false);
    const [simpleDialogVisible, setSimpleDialogVisible] = useState(false);
    const [ratioDialogVisible, setRatioDialogVisible] = useState(false);

    // To hold the currently selected link and its settings
    const [selectedSettings, setSelectedSettings] = useState<InvitationLink | null>(null);
    const [registrationType, setRegistrationType] = useState(0);
    const [registrationTypeDropdownOpen, setRegistrationTypeDropdownOpen] = useState(false);
    const [registrationTypeOptions, setRegistrationTypeOptions] = useState<{ value: number; label: string }[]>([]);
    const [localSpot, setLocalSpot] = useState<number>(0);
    const [localFutures, setLocalFutures] = useState<number>(0);
    // const [localAlpha, setLocalAlpha] = useState<number>(0);
    // const [localWeb3, setLocalWeb3] = useState<number>(0);
    const [localCode, setLocalCode] = useState<string>('');
    const [localRegistrationType, setLocalRegistrationType] = useState<number>(0);
    const [localRemark, setLocalRemark] = useState<string>('');
    const [maxSpotRatio, setMaxSpotRatio] = useState<number>(0);
    const [maxFuturesRatio, setMaxFuturesRatio] = useState<number>(0);

    // For managing share link dialog
    const [shareLinkDialogVisible, setShareLinkDialogVisible] = useState(false);

    // Simulated user and summary data
    const isLoggedIn = Boolean(typeof window !== 'undefined' && localStorage.getItem('token'));
    const isBound = true; // Replace with actual binding logic
    // API call
    const summaryData = {
        bind: true, // 是否绑定邀请码
        referral_rate: '50%', // 邀请比例
        referral_code: UserStore?.userInviteCode || '--------', // 邀请码
        invited: 10, // 推荐人数
        rewards: '100 USDT' // 获得的奖励
    };

    // Helper to build params for invitation API
    const buildInvitationParams = (code: string) => {
        let params: any = {};
        if (code !== '') {
            params.invite_code = code;
        }
        return params;
    };

    const handleReset = async () => {
        try {
            setCode('');
            setSelectedPromotionChannel(0);
            // setDataStatisticsTime([null, null]);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            // fetch new data based on the reset filters
            const res = await fetchLinkList({}, 1, pagination.pageSize);
            setInvitationLinkData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while resetting.'
            });
        }
    };

    const handleQuery = async () => {
        try {
            const params = buildInvitationParams(code);
            setPagination(prev => ({ ...prev, current: 1 })); // Reset to first page
            const res = await fetchLinkList(params, 1, pagination.pageSize);
            setInvitationLinkData(res.list);
            setTotal(res.total);
        } catch (error: any) {
            toast.error({
                message: error?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while querying.'
            });
        }
    };

    const handleSettingsDialogOpen = async (record: InvitationLink) => {
        try {
            setSelectedSettings(record);
            handleSteps('details');
        } catch (error: any) {
            toast.error({
                message: error?.message || t('invitationPromotion.invitationLink.detailsError') || 'An error occurred while fetching client details.'
            });
        }
    };

    const handleSettingsDialogClose = () => {
        setSettingsDialogVisible(false);
        setRatioDialogVisible(false);
        setSimpleDialogVisible(false);
        setSelectedSettings(null);
        setSelectedSettings(null);
    };

    const handleSteps = (step: string) => {
        switch (step) {
            case 'details':
                setStep('details');
                setSettingsDialogVisible(true);
                setSimpleDialogVisible(false);
                setRatioDialogVisible(false);
                break;
            case 'ratio':
                setStep('ratio');
                setRatioDialogVisible(true);
                setSettingsDialogVisible(true);
                setSimpleDialogVisible(false);
                break;
            case 'code':
                setStep('code');
                setSimpleDialogVisible(true);
                setSettingsDialogVisible(true);
                setRatioDialogVisible(false);
                break;
            case 'registration':
                setStep('registration');
                setSimpleDialogVisible(true);
                setSettingsDialogVisible(true);
                setRatioDialogVisible(false);
                break;
            case 'remark':
                setStep('remark');
                setSimpleDialogVisible(true);
                setSettingsDialogVisible(true);
                setRatioDialogVisible(false);
                break;
            default:
                setStep('details');
        }
    };

    const handleClose = async () => {
        // fetch new link list
        try {
            const params = buildInvitationParams(code);
            const res = await fetchLinkList(params, pagination.current, pagination.pageSize);
            setInvitationLinkData(res.list);
            setTotal(res.total);
        } catch (clientError: any) {
            toast.error({
                message: clientError?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while fetching client list.'
            });
        } finally {
            handleSteps('details');
        }
    };

    const handleSave = async () => {
        // Save logic here
        
        if (selectedSettings) {
            const params = buildInvitationParams(selectedSettings.code || '');
            if (step === 'ratio') {

                // Find the original record for comparison
                // const 
                const original = invitationLinkData.find(item => item.code === selectedSettings.code);
                if (original) {
                    // Only allow increasing ratios
                    if (
                        (localSpot ?? 0) < (original.spot ?? 0) ||
                        (localFutures ?? 0) < (original.futures ?? 0)
                        // (selectedSettings.spot ?? 0) < (original.spot ?? 0) ||
                        // (selectedSettings.futures ?? 0) < (original.futures ?? 0)
                    ) {
                        toast.error({
                            message: t('customerManagement.clientList.modifyRatioIncreaseOnly') || 'You can only increase the ratios.'
                        });
                        return;
                    }
                }

                await modifyInvitationLink({ 
                    // ...selectedSettings,
                    type: 2, // 1-Create, 2-Modify, 3-Delete
                    invite_code: selectedSettings.code, 
                    spot_rebate_rate: localSpot, 
                    contract_rebate_rate: localFutures 
                });
            }
            // not working
            if (step === 'code') {
                // Update with the new values
                await modifyInvitationLink({
                    type: 2, // 1-Create, 2-Modify, 3-Delete
                    invite_code: selectedSettings.code,
                    new_invite_code: localCode
                });
            }
            if (step === 'registration') {
                // Update with the new values
                await modifyInvitationLink({
                    type: 2, // 1-Create, 2-Modify, 3-Delete
                    invite_code: selectedSettings.code,
                    registration_type: localRegistrationType,
                    spot_rebate_rate: localSpot, 
                    contract_rebate_rate: localFutures 
                });
            }
            // not working
            if (step === 'remark') {
                // Update with the new values
                await modifyInvitationLink({
                    type: 2, // 1-Create, 2-Modify, 3-Delete
                    invite_code: selectedSettings.code,
                    spot_rebate_rate: selectedSettings.spot,
                    contract_rebate_rate: selectedSettings.futures,
                    custom_name: localRemark
                });
            }
            const res = await fetchLinkList(params, 1, 1);
            setSelectedSettings({
                ...selectedSettings,
                ...res.list.find(item => item.code === selectedSettings.code)
            });
            // Update invitationLinkData with the new values
            setInvitationLinkData(prev => prev.map(item =>
                item.code === selectedSettings.code ? { ...selectedSettings } : item
            ));
        }
        handleSteps('details');
    };

    const handleCreateCustomRegistrationPage = () => {
        navigate('/invitationPromotion/customRegistrationPage', { state: { customOpen: true } });
    };

    const handleDropdownToggle = (fieldName: string) => {
        if (fieldName === 'registrationType') {
            setRegistrationTypeDropdownOpen(prev => !prev);
        }
    };

    const handleShareLink = (record: InvitationLink) => {
        setSelectedSettings(record);
        setShareLinkDialogVisible(true);
    }

    const handleCopy = async (isCode: boolean, text: string, code?: string) => {
        try {
            // await navigator.clipboard.writeText(`https://academy.qb.finance/join/${text}`);
            await navigator.clipboard.writeText(text);
            if (isCode && code) setCopiedCode(code);
            if (!isCode) setIsCopied(true);
            setTimeout(() => {
                setCopiedCode(null);
                setIsCopied(false);
            }, 2000);
            toast.success({
                message: t('invitationPromotion.invitationLink.copySuccess') || 'UID copied!'
            });
        } catch (err: any) {
            toast.error({
                message: err?.message || t('invitationPromotion.invitationLink.copyError') || 'Failed to copy UID.'
            });
        }
    };

    const formatDate = (dateString?: string, time?: boolean) => {
        if (dateString === undefined || dateString === null || dateString === '') return t('invitationPromotion.invitationLink.none');
        if (dateString === '0') return '-';
        let date: dayjs.Dayjs;
        if (/^\d{10}$/.test(dateString)) {
            date = dayjs(Number(dateString) * 1000);
        } else {
            date = dayjs(dateString);
        }
        return time ? date.format('YYYY-MM-DD HH:mm:ss') : date.format('YYYY-MM-DD');
    };

    const formatNumber = (num: number | string | null | undefined) => {
        if (num === null || num === undefined) return '--';
        if (isNaN(Number(num))) return '--';
        // 1000000.023024 = 1,000,000.02
        return Number(num).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    };

    const queryFields: QueryField[] = [
        {
            type: 'input',
            name: 'code',
            label: t('invitationPromotion.invitationLink.code'),
            value: code,
            onChange: setCode,
            placeholder: t('invitationPromotion.invitationLink.pleaseEnterPlaceholder'),
        },
        // {
        //     type: 'dateRange',
        //     name: 'dataStatisticsTime',
        //     label: t('invitationPromotion.invitationLink.dataStatisticsTime'),
        //     value: dataStatisticsTime,
        //     onChange: (dates: [Dayjs | null, Dayjs | null] | null) => {
        //         setDataStatisticsTime(dates ?? [null, null]);
        //     },
        //     placeholder: [t('invitationPromotion.invitationLink.startTime'), t('invitationPromotion.invitationLink.endTime')],
        // }
    ];

    const invitationLinkColumns: ColumnsType<InvitationLink> = [
        {
            title: t('invitationPromotion.invitationLink.table.code'),
            dataIndex: 'code',
            key: 'code',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text, record) => (
                <div className="flex flex-col items-start gap-2">
                    <div className="flex flex-row gap-2">
                        <span>{text}</span>
                        <button 
                            className="text-sm font-normal text-special-text font-inter bg-transparent border-none flex justify-center items-center"
                            onClick={() => handleCopy(true, text, record.code)}
                        >
                            {copiedCode === record.code ? (
                                <img src={isDark ? CheckDarkIcon : CheckIcon} className="w-4 h-4" />
                            ) : (
                                <img src={isDark ? CopyDarkIcon : CopyIcon} className="w-4 h-4" />
                            )}
                        </button>
                    </div>
                    {record.isDefault && (
                        <span className="px-2 py-0.5 text-xs font-normal text-sel-normal dark:text-dark-sel-normal bg-button dark:bg-dark-button rounded-sm">
                            {t('invitationPromotion.invitationLink.table.default')}
                        </span>
                    )}
                </div>
            )
        },
        {
            title: t('invitationPromotion.invitationLink.table.spot'),
            dataIndex: 'spot',
            key: 'spot',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => <span>{maxSpotRatio - text}%</span>
        },
        {
            title: t('invitationPromotion.invitationLink.table.futures'),
            dataIndex: 'futures',
            key: 'futures',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => <span>{maxFuturesRatio - text}%</span>
        },
            // {
            //     title: t('invitationPromotion.invitationLink.table.alpha'),
            //     dataIndex: 'alpha',
            //     key: 'alpha',
            //     align: 'start',
            //     width: 150,
            //     ellipsis: true,
            //     render: (text) => <span>{text}%</span>
            // },
        {
            title: t('invitationPromotion.invitationLink.table.linkLocation'),
            dataIndex: 'linkLocation',
            key: 'linkLocation',
            align: 'start',
            width: 120,
            ellipsis: true,
            render: (text) => <span>{text !== 0 ? text: '-' }</span>
        },
        // {
        //     title: t('invitationPromotion.invitationLink.table.clickCount'),
        //     dataIndex: 'clickCount',
        //     key: 'clickCount',
        //     align: 'start',
        //     width: 130,
        //     ellipsis: true
        // },
        {
            title: t('invitationPromotion.invitationLink.table.registrationCount'),
            dataIndex: 'registrationCount',
            key: 'registrationCount',
            align: 'start',
            width: 160,
            ellipsis: true,
        },
        {
            title: t('invitationPromotion.invitationLink.table.depositCount'),
            dataIndex: 'depositCount',
            key: 'depositCount',
            align: 'start',
            width: 160,
            ellipsis: true,
        },
        {
            title: t('invitationPromotion.invitationLink.table.tradeCount'),
            dataIndex: 'tradeCount',
            key: 'tradeCount',
            align: 'start',
            width: 160,
            ellipsis: true,
        },
        {
            title: t('invitationPromotion.invitationLink.table.tradingVolume'),
            dataIndex: 'tradingVolume',
            key: 'tradingVolume',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{formatNumber(text)}</span>
        },
        {
            title: t('invitationPromotion.invitationLink.table.rebateAmount'),
            dataIndex: 'rebateAmount',
            key: 'rebateAmount',
            align: 'start',
            width: 160,
            ellipsis: true,
            render: (text) => <span className="break-all whitespace-normal">{formatNumber(text)}</span>
        },
        {
            title: t('invitationPromotion.invitationLink.table.operation'),
            dataIndex: 'operation',
            key: 'operation',
            align: 'end',
            width: 140,
            fixed: 'right',
            render: (_, record) => (
                <div className="grid grid-cols-3 md:flex items-center justify-start md:justify-end gap-2">
                    <button
                        className="text-sm font-normal text-special-text font-inter md:w-14 bg-transparent border-none flex justify-center items-center"
                        onClick={() => handleShareLink(record)}
                    >
                        {t('invitationPromotion.invitationLink.table.shareLink')}
                    </button>
                </div>
            )
        }
    ];

    const mobileInvitationLinkColumns = invitationLinkColumns
        .filter((col: any) => 'dataIndex' in col)
        .map((col: any) =>
            col.dataIndex === 'operation' ? { ...col, isOperation: true } : col
    );

    useEffect(() => {
        // Get dropdown list
        setRegistrationTypeOptions([
            { value: 0, label: t('invitationPromotion.invitationLink.table.default') },
            { value: 1, label: t('invitationPromotion.invitationLink.table.custom') }
        ]);
    }, []);

    useEffect(() => {
        if (step === 'ratio' && selectedSettings) {
            setLocalSpot(selectedSettings.spot);
            setLocalFutures(selectedSettings.futures);
            // setLocalAlpha(selectedSettings.alpha);
            // setLocalWeb3(selectedSettings.web3);
        }

        if (step === 'code' && selectedSettings) {
            setLocalCode(selectedSettings.code);
        }

        if (step === 'registration' && selectedSettings) {
            setLocalRegistrationType(selectedSettings.registrationType);
        }

        if (step === 'remark' && selectedSettings) {
            setLocalRemark(selectedSettings.remarks ?? '');
        }
    }, [step, selectedSettings]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                setMaxSpotRatio(UserStore.userSpotRatio); // Example max spot ratio
                setMaxFuturesRatio(UserStore.userRewardRatio); // Example max futures ratio

                // try {
                //     const groupRes = await fetchGroupID({});
                //     setCustomerGroups(groupRes);
                // } catch (groupError: any) {
                //     toast.error({
                //         message: groupError?.message || t('invitationPromotion.invitationLink.groupFetchError') || 'An error occurred while fetching group list.'
                //     });
                // }

                try {
                    const params = buildInvitationParams(code);
                    const res = await fetchLinkList(params, pagination.current, pagination.pageSize);
                    setInvitationLinkData(res.list);
                    setTotal(res.total);
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while fetching client list.'
                    });
                }
                setLastUpdated(Math.floor(Date.now() / 1000));
            } catch (error: any) {
                toast.error({
                    message: error?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while fetching data.'
                });
            } finally {
                setLoading(false);
            }
        };
        if (!selectedSettings) {
            fetchData();
        } else {
            // Fetch client history commissions when viewing details
            const fetchHistoryData = async () => {
                setLoading(true);
                try {
                    // const res = await fetchInviteSetting({ code: selectedSettings.code }, pagination.current, pagination.pageSize);
                    // setInvitationSettingsData(res.list);
                    // setTotal(res.total);
                } catch (error: any) {
                    toast.error({
                        message: error?.message || t('invitationPromotion.invitationLink.fetchError') || 'An error occurred while fetching data.'
                    });
                } finally {
                    setLoading(false);
                }
            };
            fetchHistoryData();
        }
    }, [pagination.current, pagination.pageSize]);

    return (
        <div className="bg-mainLayout dark:bg-dark-mainLayout min-h-screen px-6 pt-6 pb-40 md:pb-6">
            <GenericQueryCard
                title={t('invitationPromotion.invitationLink.title')}
                subtitle={t('invitationPromotion.invitationLink.dataUpdatedAt').replace('{time}', lastUpdated ? new Date(lastUpdated * 1000).toLocaleString() : '2025-07-11 20:00:00')}
                fields={queryFields}
                onReset={handleReset}
                onQuery={handleQuery}
                gridCols="grid-cols-2 md:grid-cols-4"
            />
            
            {/* Table Container with Custom Layout */}
            <div className="mt-6 bg-card-background dark:bg-card-background rounded-lg p-6 overflow-hidden">
                {/* Table Content */}
                <div className="hidden md:block h-[556px] overflow-hidden overflow-y-auto overflow-x-auto">
                    <Table
                        dataSource={invitationLinkData}
                        columns={invitationLinkColumns}
                        rowKey="code"
                        pagination={false}
                        loading={loading}
                        size="small"
                        // scroll={{ x: 1200 }}
                        className="custom-action-table"
                        showHeader={true}
                        locale={{ emptyText: '' }}
                        components={{
                            body: {
                                wrapper: (props: any) => <tbody {...props} className="bg-card-background dark:bg-card-background" />,
                            }
                        }}
                    />
                </div>
                <div className="block md:hidden">
                    {invitationLinkData.map((row, idx) => (
                        <MobileTableCard
                            key={row.code}
                            record={row}
                            columns={mobileInvitationLinkColumns}
                            t={t}
                        />
                    ))}
                </div>
                <div className="mt-4 flex justify-end">
                    <Pagination
                        currentPage={pagination.current}
                        totalPages={Math.ceil(total / pagination.pageSize)}
                        onPageChange={(page: number) => setPagination({ ...pagination, current: page })}
                        pageSize={pagination.pageSize}
                        setPageSize={(size: number) => setPagination({ ...pagination, pageSize: size, current: 1 })}
                        pageSizes={[10, 20, 50, 100]}
                        isPageSizeVisible={true}
                        disabled={loading}
                    />
                </div>
            </div>

            <InviteFriendDialog
                visible={shareLinkDialogVisible}
                onClose={() => setShareLinkDialogVisible(false)}
                inviteCode={selectedSettings?.code ?? ''}
                isCopied={isCopied}
                isLoggedIn={isLoggedIn}
                summaryData={summaryData}
                isBound={isBound}
            />

            {/* Settings Dialog */}
            <Dialog
                visible={settingsDialogVisible}
                onClose={() => handleSettingsDialogClose()}
                title={
                    <div className="text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal">
                        {t('invitationPromotion.invitationLink.settings.title')}
                    </div>
                }
                footer={null}
                width="750px"
                center={false}
                mobileWidth="90%"
                closeOnClickModal={true}
                showClose={true}
                backdropBlur={3}
                customClass="p-6"
                bodyStyle={{ padding: 0 }}
                headerStyle={{ padding: 0 }}
                footerStyle={{ padding: 0 }}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-6">
                    <div className="flex flex-col items-start gap-3">
                        <div className="text-sel-normal dark:text-dark-sel-normal text-lg font-normal font-inter leading-normal">
                            {t('invitationPromotion.invitationLink.settings.invitationLink')}
                        </div>
                        <div className="w-full flex-col border border-normal rounded-lg px-6 py-4">
                            <div className="flex justify-end">
                                <img src={LineBarIcon} alt="Line Bar" className="cursor-pointer" onClick={() => handleSteps('ratio')} />
                            </div>
                            {/* md:grid-cols-4 */}
                            <div className="grid grid-cols-2 gap-4 items-start text-center">
                                <div className="flex flex-col">
                                    <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                                        {typeof selectedSettings?.spot === 'number'
                                            ? (selectedSettings.spot % 1 !== 0 && selectedSettings.spot.toString().split('.')[1]?.length > 2
                                                ? (maxSpotRatio - selectedSettings.spot).toFixed(2)
                                                : maxSpotRatio - selectedSettings.spot)
                                            : 0}%
                                    </span>
                                    <span className="text-xs font-normal font-inter leading-normal text-normal dark:text-dark-normal">{t('invitationPromotion.invitationLink.settings.spot')}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xl font-normal font-inter leading-normal text-sel-normal dark:text-dark-sel-normal">
                                        {typeof selectedSettings?.futures === 'number'
                                            ? (selectedSettings.futures % 1 !== 0 && selectedSettings.futures.toString().split('.')[1]?.length > 2
                                                ? (maxFuturesRatio - selectedSettings.futures).toFixed(2)
                                                : maxFuturesRatio - selectedSettings.futures)
                                            : 0}%
                                    </span>
                                    <span className="text-xs font-normal font-inter leading-normal text-normal dark:text-dark-normal">{t('invitationPromotion.invitationLink.settings.futures')}</span>
                                </div>
                                {/* Alpha and Web3 Ratio */}
                            </div>
                        </div>
                        <div className="w-full flex flex-col gap-2">
                            <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.link')}
                                </div>
                                <div className="flex gap-1">
                                    <span
                                        className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none max-w-[100px] truncate inline-block align-bottom"
                                        title={selectedSettings?.link || '--'}
                                    >
                                        {selectedSettings?.link || '--'}
                                    </span>
                                    <button
                                        onClick={() => handleCopy(false, selectedSettings?.link || '')}
                                        className="hover:bg-hover dark:hover:bg-dark-hover rounded-full transition-colors"
                                    >
                                        <img
                                            src={isCopied ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)} 
                                            alt={isCopied ? 'copied' : 'copy'}
                                            className="cursor-pointer w-4 h-4" 
                                        />
                                    </button>
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.code')}
                                </div>
                                <div className="flex gap-1">
                                    <span
                                        className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none max-w-[100px] truncate inline-block align-bottom"
                                        title={selectedSettings?.code || '--'}
                                    >
                                        {selectedSettings?.code || '--'}
                                    </span>
                                    {/* <button
                                        onClick={() => handleSteps('code')}
                                        className="hover:bg-hover dark:hover:bg-dark-hover rounded-full transition-colors"
                                    >
                                        <img
                                            src={PencilIcon} 
                                            alt={'Edit Code'}
                                            className="cursor-pointer w-4 h-4" 
                                        />
                                    </button> */}
                                    <button
                                        onClick={() => handleCopy(true, selectedSettings?.code || '', selectedSettings?.code)}
                                        className="hover:bg-hover dark:hover:bg-dark-hover rounded-full transition-colors"
                                    >
                                        <img
                                            src={copiedCode === selectedSettings?.code ? (isDark ? CheckDarkIcon : CheckIcon) : (isDark ? CopyDarkIcon : CopyIcon)}
                                            alt={copiedCode === selectedSettings?.code ? 'copied' : 'copy'}
                                            className="cursor-pointer w-4 h-4"
                                        />
                                    </button>
                                </div>
                            </div>
                            {/* <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.registrationType')}
                                </div>
                                <div className="flex gap-1">
                                    <span
                                        className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none max-w-[100px] truncate inline-block align-bottom"
                                        title={
                                            registrationTypeOptions.find(
                                                option => option.value === (selectedSettings?.registrationType ?? 0)
                                            )?.label || '--'
                                        }
                                    >
                                        {
                                            registrationTypeOptions.find(
                                                option => option.value === (selectedSettings?.registrationType ?? 0)
                                            )?.label || '--'
                                        }
                                    </span>
                                    <button
                                        onClick={() => handleSteps('registration')}
                                        className="hover:bg-hover dark:hover:bg-dark-hover rounded-full transition-colors"
                                    >
                                        <img
                                            src={ArrowRightIcon} 
                                            alt={'Edit Registration Type'}
                                            className="cursor-pointer w-4 h-4" 
                                        />
                                    </button>
                                </div>
                            </div> */}
                            {/* <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.remarks')}
                                </div>
                                <div className="flex gap-1">
                                    <span
                                        className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none max-w-[100px] truncate inline-block align-bottom"
                                        title={selectedSettings?.remarks || '--'}
                                    >
                                        {selectedSettings?.remarks || '--'}
                                    </span>
                                    <button
                                        onClick={() => handleSteps('remark')}
                                        className="hover:bg-hover dark:hover:bg-dark-hover rounded-full transition-colors"
                                    >
                                        <img
                                            src={PencilIcon} 
                                            alt={'Edit Remark'}
                                            className="cursor-pointer w-4 h-4" 
                                        />
                                    </button>
                                </div>
                            </div> */}
                        </div>
                    </div>
                    <div className="flex flex-col items-start gap-3">
                        <div className="text-sel-normal dark:text-dark-sel-normal text-lg font-normal font-inter leading-normal">
                            {t('invitationPromotion.invitationLink.settings.dataOverview')}
                        </div>
                        <div className="w-full flex flex-col gap-2">
                            <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.rebateAmount')}
                                </div>
                                <div className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none">
                                    {selectedSettings?.rebateAmount || 0}
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.tradingVolume')}
                                </div>
                                <div className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none">
                                    {selectedSettings?.tradingVolume || 0}
                                </div>
                            </div>
                            {/* horizontal line */}
                            <div className="border-t border-normal" />

                            {/* <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.numberOfClicks')}
                                </div>
                                <div className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none">
                                    {selectedSettings?.clickCount || 0}
                                </div>
                            </div> */}
                            <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.numberOfInvites')}
                                </div>
                                <div className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none">
                                    {selectedSettings?.registrationCount || 0}
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.numberOfTraders')}
                                </div>
                                <div className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none">
                                    {selectedSettings?.tradeCount || 0}
                                </div>
                            </div>
                            <div className="flex justify-between">
                                <div className="text-normal dark:text-dark-normal text-xs font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.numberOfDepositors')}
                                </div>
                                <div className="text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-none">
                                    {selectedSettings?.depositCount || 0}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* Simple Dialog for code, registration, and remark */}
            <Dialog
                visible={simpleDialogVisible}
                onClose={() => handleClose()}
                title={(step === 'registration') ? 
                    <div className="text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal">
                        {t('invitationPromotion.invitationLink.settings.editRegistrationType')}
                    </div> : <></>}
                footer={null}
                width="500px"
                center={false}
                mobileWidth="90%"
                closeOnClickModal={true}
                showClose={true}
                backdropBlur={3}
                customClass="p-6"
                bodyStyle={{ padding: 0 }}
                headerStyle={{ padding: 0 }}
                footerStyle={{ padding: 0 }}
            >
                <>
                    {(step === 'registration') &&
                        <div className="flex flex-col gap-4 pt-4">
                            <div className="flex flex-col gap-[6px]">
                                <div className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">
                                    {t('invitationPromotion.invitationLink.settings.registrationType')}
                                </div>
                                <div className={`relative z-10 w-full`} onClick={e => e.stopPropagation()}>
                                    <div
                                        className={
                                            `flex items-center cursor-pointer h-8 px-2 bg-card-background dark:bg-dark-card-background border border-normal dark:border-dark-normal rounded-md w-full`
                                        }
                                        onClick={() => handleDropdownToggle('registrationType')}
                                    >
                                        <div className="flex-1 text-xs font-inter font-normal text-sel-normal dark:text-dark-sel-normal truncate">
                                              {
                                                registrationTypeOptions.find(
                                                    option => option.value === localRegistrationType
                                                )?.label || t('invitationPromotion.invitationLink.settings.notSelected')
                                            }
                                        </div>
                                        <div className="ml-1 flex items-center">
                                            <svg className={`h-3 w-3 transition-transform duration-300 ${registrationTypeDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="#A1A3C4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </div>
                                    </div>
                                    <div className={`absolute left-0 mt-1 p-[10px] bg-button dark:bg-dark-button rounded-md shadow-lg z-30 min-w-full transition-all border border-normal duration-300 origin-top inline-flex flex-col justify-start items-start ${registrationTypeDropdownOpen ? 'opacity-100 scale-y-100 transform' : 'opacity-0 scale-y-0 transform pointer-events-none'}`}> 
                                        {(registrationTypeOptions || []).map(option => (
                                            <div
                                                key={option.value}
                                                className={`self-stretch h-6 py-1 inline-flex justify-start items-center cursor-pointer hover:bg-button-hover dark:hover:bg-dark-button-hover ${option.value === registrationType ? 'text-sel-normal dark:text-dark-sel-normal' : 'text-normal dark:text-dark-normal'}`}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setLocalRegistrationType(option.value);
                                                    setRegistrationTypeDropdownOpen(false);
                                            }}
                                            >
                                                <div className="justify-start text-xs font-inter font-normal leading-tight truncate">
                                                    {option.label}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-start">
                                <button 
                                    className="bg-card-background dark:bg-dark-card-background border border-main text-sel-normal dark:text-dark-sel-normal opacity-60 text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded"
                                    onClick={handleCreateCustomRegistrationPage}
                                >
                                    {t('invitationPromotion.invitationLink.settings.customRegistrationPage')}
                                </button>
                            </div>
                            <div className="flex justify-end">
                                <button 
                                    className="bg-tabline dark:bg-dark-tabline text-sel-normal dark:text-dark-sel-normal hover:bg-hover-bg dark:hover:bg-dark-hover-bg text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded"
                                    onClick={handleSave}
                                >
                                    {t('invitationPromotion.invitationLink.settings.confirm')}
                                </button>
                            </div>
                        </div>
                    }
                    {/* {(step === 'code') &&
                        <div className="flex flex-col gap-6 pt-[14px]">
                            <div className="text-center text-sel-normal dark:text-dark-sel-normal text-xl font-normal font-inter leading-none">
                                {t('invitationPromotion.invitationLink.settings.changeInvitationCode')}
                            </div>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder={t('invitationPromotion.invitationLink.settings.clientLabelPlaceholder')}
                                    className="bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter"
                                    maxLength={8}
                                    onChange={e => setLocalCode(e.target.value)}
                                    value={localCode || '--'}
                                />
                                <span className="absolute right-3 top-[15px] text-chart-title-text text-sm font-medium font-sans leading-tight">{(localCode || '').length}/8</span>
                            </div>
                            <div className="flex justify-end gap-4">
                                <button 
                                    className="bg-card-background dark:bg-dark-card-background border border-dark-main dark:border-main opacity-60 hover:opacity-50 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded"
                                    onClick={handleClose}
                                >
                                    {t('invitationPromotion.invitationLink.settings.cancel')}
                                </button>
                                <button 
                                    className="bg-tabline dark:bg-dark-tabline text-sel-normal dark:text-dark-sel-normal hover:bg-hover-bg dark:hover:bg-dark-hover-bg text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded"
                                    onClick={handleSave}
                                >
                                    {t('invitationPromotion.invitationLink.settings.confirm')}
                                </button>
                            </div>
                        </div>
                    } */}
                    {(step === 'remark') &&
                        <div className="flex flex-col gap-6 pt-[14px]">
                            <div className="text-center text-sel-normal dark:text-dark-sel-normal text-xl font-normal font-inter leading-none">
                                {t('invitationPromotion.invitationLink.settings.remarks')}
                            </div>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder={t('invitationPromotion.invitationLink.settings.remarksPlaceholder')}
                                    className="bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal focus:ring-0 focus:outline-none rounded-lg px-3 py-[15px] w-full text-sel-normal dark:text-dark-sel-normal text-xs font-medium font-inter"
                                    maxLength={100}
                                    onChange={e => setLocalRemark(e.target.value)}
                                    value={localRemark}
                                />
                                <span className="absolute right-3 top-[15px] text-chart-title-text text-sm font-medium font-sans leading-tight">{(localRemark || '').length}/100</span>
                            </div>
                            <div className="flex justify-end gap-4">
                                <button 
                                    className="bg-card-background dark:bg-dark-card-background border border-dark-main dark:border-main opacity-60 hover:opacity-50 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded"
                                    onClick={handleClose}
                                >
                                    {t('invitationPromotion.invitationLink.settings.cancel')}
                                </button>
                                <button 
                                    className="bg-tabline dark:bg-dark-tabline text-sel-normal dark:text-dark-sel-normal hover:bg-hover-bg dark:hover:bg-dark-hover-bg text-xs font-normal font-inter leading-tight px-4 py-1.5 rounded"
                                    onClick={handleSave}
                                >
                                    {t('invitationPromotion.invitationLink.settings.confirm')}
                                </button>
                            </div>
                        </div>
                    }
                </>
            </Dialog>

            {/* Ratio Dialog */}
            <Dialog
                visible={ratioDialogVisible}
                onClose={() => handleClose()}
                title={
                    <div className="text-sel-normal dark:text-dark-sel-normal text-2xl font-normal font-inter leading-normal">
                        {t('invitationPromotion.invitationLink.settings.modifyRebateRatio')}
                    </div>
                }
                footer={null}
                width="500px"
                center={false}
                mobileWidth="90%"
                closeOnClickModal={true}
                showClose={true}
                backdropBlur={3}
                customClass="p-6"
                bodyStyle={{ padding: 0 }}
                headerStyle={{ padding: 0 }}
                footerStyle={{ padding: 0 }}
            >
                {/* Commission Settings Section */}
                <div className="flex-col">

                    {/* Spot Trading */}
                    <div className="space-y-3 my-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.invitationLink.settings.spot')}</span>
                        <div className="flex items-center gap-2">
                            <Input
                                placeholder={t('invitationPromotion.invitationLink.settings.clientRatio')}
                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                onChange={e => {
                                    let value = e.target.value.replace(/\D/g, '');
                                    let num = Number(value);
                                    if (num > maxSpotRatio) num = maxSpotRatio;
                                    setLocalSpot(num);
                                }}
                                value={localSpot}
                            />
                            <div>+</div>
                            <Input
                                placeholder={t('invitationPromotion.invitationLink.settings.clientRatio')}
                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                // value={MAX_PERCENTAGE - (selectedRecord?.spotRatio ?? 0)}
                                value={maxSpotRatio - localSpot}
                                disabled={true}
                            />
                            <div>=</div>
                            <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxSpotRatio}%</span>
                        </div>
                    </div>

                    {/* Futures Trading */}
                    <div className="space-y-3 mb-4 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg p-4">
                        <span className="text-sel-normal dark:text-dark-sel-normal text-sm font-normal font-inter leading-none">{t('invitationPromotion.invitationLink.settings.futures')}</span>
                        <div className="flex items-center gap-2">
                            <Input
                                placeholder={t('invitationPromotion.invitationLink.settings.clientRatio')}
                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                className="flex flex-1 bg-card-background dark:bg-card-background border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                onChange={e => {
                                    let value = e.target.value.replace(/\D/g, '');
                                    let num = Number(value);
                                    if (num > maxFuturesRatio) num = maxFuturesRatio;
                                    setLocalFutures(num);
                                }}
                                value={localFutures}
                            />
                            <div>+</div>
                            <Input
                                placeholder={t('invitationPromotion.invitationLink.settings.myRatio')}
                                suffix={<span className="ml-[2px] text-normal dark:text-dark-normal text-sm font-medium font-sans leading-tight">%</span>}
                                className="flex flex-1 bg-button-hover dark:bg-dark-button-hover border border-normal dark:border-dark-normal rounded-lg px-3 py-[15px] text-sel-normal dark:text-dark-sel-normal text-xs"
                                // value={MAX_PERCENTAGE - (selectedRecord?.futuresRatio ?? 0)}
                                value={maxFuturesRatio - localFutures}
                                disabled={true}
                            />
                            <div>=</div>
                            <span className="text-special-text text-sm font-medium font-sans leading-tight">{maxFuturesRatio}%</span>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-2">
                    <button
                        onClick={handleClose}
                        className="bg-transparent border border-dark-main dark:border-main opacity-60 rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                    >
                        {t('invitationPromotion.invitationLink.settings.cancel')}
                    </button>
                    <button
                        onClick={() => {
                            console.log('Save client settingsment settings');
                            handleSave();
                        }}
                        className="bg-tabline border-none rounded-lg px-6 py-2 text-sel-normal dark:text-dark-sel-normal text-xs font-normal font-inter leading-tight cursor-pointer"
                    >
                        {t('invitationPromotion.invitationLink.settings.confirm')}
                    </button>
                </div>
            </Dialog>
        </div>
    );
};

export default InvitationLink;
